import * as React from "react";
import { Box, Stack, Grid } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTitle } from "../../hooks/useTitle";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { setUser } from "../auth/authSlice";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../hooks/store";
import { useEditAdminMutation } from "../../services/admin.service";
import LoadingButton from "@mui/lab/LoadingButton";
import { CardBase, StyledTextField } from "../../components/global";

const AccountPage = () => {
  useTitle("Account");
  const dispatch = useAppDispatch();

  const {
    handleSubmit,
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm();

  const [edit, { isLoading: editing }] = useEditAdminMutation();

  const data = useCurrentUser();

  const onSubmit = async (payload: any) => {
    try {
      if (!payload.password) delete payload.password;
      payload.id = data?.id!;
      await edit(payload).unwrap();
      toast.success("Account updated");
      dispatch(setUser(payload));
    } catch (err) { }
  };

  React.useEffect(() => {
    if (data) {
      setValue("first_name", data?.first_name || "");
      setValue("last_name", data?.last_name || "");
      setValue("password", "");
      setValue("email", data?.email || "");
      clearErrors();
    }
  }, [data, setValue, clearErrors]);

  return (
    <Grid container spacing={{ xs: 2, sm: 3 }}>
      <Grid item xs={12} sm={10} md={8} lg={6}>
        <CardBase
          title="Account Settings"
          subtitle="Update your account information"
          useGlassCard={false}
        >
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            sx={{ width: "100%" }}
          >
            <Stack spacing={{ xs: 2, sm: 2.5 }}>
              <Controller
                name="first_name"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <StyledTextField
                    label="First Name"
                    value={value || ""}
                    fullWidth
                    onChange={onChange}
                    onBlur={onBlur}
                    inputRef={ref}
                    error={!!errors.first_name}
                    helperText={
                      !!errors.first_name && "This field is required"
                    }
                  />
                )}
              />
              <Controller
                name="last_name"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <StyledTextField
                    label="Last Name"
                    value={value || ""}
                    fullWidth
                    onChange={onChange}
                    onBlur={onBlur}
                    inputRef={ref}
                    error={!!errors.last_name}
                    helperText={!!errors.last_name && "This field is required"}
                  />
                )}
              />
              <Controller
                name="password"
                control={control}
                rules={{ required: !!data ? false : true }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <StyledTextField
                    label="Password"
                    value={value || ""}
                    fullWidth
                    type="password"
                    onChange={onChange}
                    onBlur={onBlur}
                    inputRef={ref}
                    error={!!errors.password}
                    helperText={
                      !data
                        ? (!!errors.password && "This field is required")
                        : "Leave blank if you don't want to change the password"
                    }
                  />
                )}
              />

              <Box
                sx={{
                  mt: { xs: 1, sm: 2 },
                  display: "flex",
                  justifyContent: "flex-start",

                  gap: { xs: 1.5, sm: 2 },
                }}
              >
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={editing}
                  size="large"
                  sx={{
                    minWidth: { xs: 100, sm: 120 },
                    backgroundColor: "#F4A259",
                    color: "#ffffff",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#E8944A",
                    },
                  }}
                >
                  Update
                </LoadingButton>
              </Box>
            </Stack>
          </Box>
        </CardBase>
      </Grid>
    </Grid>
  );
};
export default AccountPage;
